import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Map } from "lucide-react";

function NamedCRUD({ endpoint, title, emptyText, icon: Icon, testIdPrefix }) {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(endpoint);
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [endpoint]);

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setAdding(true);
    try {
      await api.post(endpoint, { name: name.trim() });
      setName("");
      toast.success(`${title} añadido`);
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setAdding(false);
    }
  };

  const remove = async (id) => {
    if (!confirm("¿Eliminar?")) return;
    try {
      await api.delete(`${endpoint}/${id}`);
      toast.success("Eliminado");
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-4">
      <form onSubmit={add} className="flex gap-2">
        <input
          data-testid={`${testIdPrefix}-name-input`}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={`Nuevo ${title.toLowerCase()}`}
          className="flex-1 rounded-xl border border-neutral-300 bg-white px-4 h-11 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
        />
        <button data-testid={`${testIdPrefix}-add-button`} disabled={adding} className="btn-primary">
          <Plus className="h-4 w-4" />
          Añadir
        </button>
      </form>
      {loading ? (
        <div className="card-surface p-8 text-center text-neutral-500">Cargando…</div>
      ) : items.length === 0 ? (
        <div className="card-surface p-10 text-center">
          <Icon className="h-10 w-10 mx-auto text-neutral-300 mb-3" />
          <div className="text-sm text-neutral-500">{emptyText}</div>
        </div>
      ) : (
        <div className="card-surface divide-y divide-neutral-100">
          {items.map((it) => (
            <div
              key={it.id}
              data-testid={`${testIdPrefix}-row-${it.id}`}
              className="flex items-center justify-between px-4 py-3"
            >
              <div className="font-medium">{it.name}</div>
              <button
                data-testid={`${testIdPrefix}-delete-${it.id}`}
                onClick={() => remove(it.id)}
                className="btn-ghost text-red-600 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CircuitsTab() {
  return (
    <NamedCRUD
      endpoint="/circuits"
      title="Circuito"
      emptyText="Aún no hay circuitos."
      icon={Map}
      testIdPrefix="circuit"
    />
  );
}
